import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import Link from "next/link";
import { useCallback, useState } from "react";
import { post } from "axios";
import { FiLock } from "react-icons/fi";

export const NEWSLETTER_SUBSCRIBED_KEY = "wdNewsletterSubscribed";
export const NEWSLETTER_SESSION_KEY = "wdArticleNewsletterPopup";

const storageGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
};

const storageSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (e) {
    // Ignore storage errors
  }
};

export const hasNewsletterSubscription = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return (
    storageGet(window.localStorage, NEWSLETTER_SUBSCRIBED_KEY) === "1" ||
    storageGet(window.localStorage, NEWSLETTER_SESSION_KEY) === "subscribed"
  );
};

export const markNewsletterSubscribed = () => {
  if (typeof window === "undefined") {
    return;
  }
  storageSet(window.localStorage, NEWSLETTER_SUBSCRIBED_KEY, "1");
};

export const hasSeenNewsletterThisSession = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return Boolean(storageGet(window.sessionStorage, NEWSLETTER_SESSION_KEY));
};

export const markNewsletterSeenThisSession = () => {
  if (typeof window === "undefined") {
    return;
  }
  storageSet(window.sessionStorage, NEWSLETTER_SESSION_KEY, "1");
};

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const ArticleNewsletterModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const title = `Get helpful insights on mind & body`;

  const submitEmail = useCallback(async () => {
    if (submitted || submitting) {
      return;
    }

    setError(null);
    if (!email.trim()) {
      setError("Please enter an email");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Please enter a valid email");
      return;
    }

    setSubmitting(true);
    try {
      await post(`${process.env.NEXT_PUBLIC_NEW_EMAIL_URL}/list`, {
        email: email.trim(),
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "newsletter_signup",
        formId: "article-newsletter-modal",
      });
      markNewsletterSubscribed();
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError("Something went wrong, please try again");
    } finally {
      setSubmitting(false);
    }
  }, [email, submitted, submitting]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        mx="16px"
        maxW="520px"
        borderRadius="16px"
        px={{ base: "24px", md: "40px" }}
        py={{ base: "32px", md: "40px" }}
        pt={{ base: "48px", md: "56px" }}
      >
        <ModalCloseButton top="16px" right="16px" color="gray.500" />
        <ModalBody p={0}>
          <Heading
            as="h2"
            textStyle="h2"
            fontSize={{ base: "28px", md: "36px" }}
            lineHeight="1.2"
            textAlign="center"
            mb="16px"
          >
            {title}
          </Heading>
          <Text
            textStyle="body1"
            textAlign="center"
            mb={{ base: "24px", md: "32px" }}
          >
            Subscribe to our Welldoing newsletter for evidence-based articles
            and therapist tips by email. Join over 30,000 other subscribers.
          </Text>
          {submitted ? (
            <Text
              textStyle="h5bold"
              textAlign="center"
              color="brand.400"
              py="16px"
            >
              You&apos;re all set for our newsletter!
            </Text>
          ) : (
            <>
              <Text
                as="label"
                htmlFor="article-newsletter-email"
                textStyle="h6"
                fontWeight="600"
                display="block"
                mb="8px"
              >
                Email address
              </Text>
              <Input
                id="article-newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitEmail();
                  }
                }}
                placeholder="e.g. youremail@example.com"
                autoComplete="email"
                h="48px"
                bg="white"
                borderRadius="10px"
                borderColor="#E6DFD8"
                _placeholder={{ color: "gray.400" }}
                _hover={{ borderColor: "brand.200" }}
                _focus={{
                  borderColor: "brand.400",
                  boxShadow: "none",
                }}
                isInvalid={error !== null}
              />
              {error && (
                <Text color="red.500" textStyle="footer2" mt="8px">
                  {error}
                </Text>
              )}
              <Button
                w="100%"
                h="48px"
                mt="16px"
                mb={0}
                mx={0}
                onClick={submitEmail}
                isLoading={submitting}
              >
                Subscribe to our Welldoing newsletter
              </Button>
            </>
          )}
          <Divider mt="24px" mb="16px" borderColor="#EDE8E2" />
          <Flex justify="center" align="flex-start" gap="8px">
            <Box mt="3px" color="gray.500" flexShrink={0}>
              <FiLock size={14} />
            </Box>
            <Text
              textStyle="footer2"
              color="gray.500"
              textAlign="center"
              maxW="360px"
            >
              We respect your privacy. By subscribing, you agree to our{" "}
              <Link
                href="/privacy-policy"
                target="_blank"
                prefetch={false}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                href="/terms-and-conditions"
                target="_blank"
                prefetch={false}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                Terms of Use
              </Link>
              .
            </Text>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ArticleNewsletterModal;
